/**
 * Graph Type Definitions
 * 
 * These types define the structure of the knowledge graph visualization system.
 * They are designed to be extensible for future node types like todos and tasks.
 */

// Graph scope
export type GraphScope = 'personal' | 'organization';

// Base node types - extensible for future additions
export enum NodeType {
  MESSAGE = 'message',
  CONVERSATION = 'conversation',
  DOCUMENT = 'document',
  CONCEPT = 'concept',
  PERSON = 'person',
  TOPIC = 'topic',
  ORGANIZATION = 'organization',
  // Future additions:
  // TODO = 'todo',
  // TASK = 'task',
  // PROJECT = 'project',
  // EVENT = 'event',
}

// Edge relationship types - extensible for todo relationships
export enum EdgeType {
  REFERENCES = 'references',
  MENTIONS = 'mentions',
  REPLIES_TO = 'replies_to',
  RELATED_TO = 'related_to',
  TAGGED_WITH = 'tagged_with',
  BELONGS_TO = 'belongs_to',
  CREATED_BY = 'created_by',
  // Semantic relationship types:
  SUPPORTS = 'supports',
  CONTRADICTS = 'contradicts',
  CAUSES = 'causes',
  DEPENDS_ON = 'depends_on',
  // Future todo relationships:
  // BLOCKS = 'blocks',
  // SUBTASK_OF = 'subtask_of',
  // ASSIGNED_TO = 'assigned_to',
  // DUE_ON = 'due_on',
}

// Semantic edge metadata
export interface SemanticEdgeMetadata {
  relationshipType: string;
  confidence: number;
  verified?: boolean;
  detectionMethod?: string;
}

// Base node interface
export interface BaseNode {
  id: string;
  type: NodeType;
  label: string;
  namespace: string;
  createdAt: Date;
  updatedAt: Date;
  metadata?: Record<string, any>;
}

// Specific node types
export interface MessageNode extends BaseNode {
  type: NodeType.MESSAGE;
  properties: {
    content: string;
    conversationId: string;
    userId: string;
    agentId?: string;
    role: 'user' | 'assistant';
    timestamp: Date;
  };
}

export interface DocumentNode extends BaseNode {
  type: NodeType.DOCUMENT;
  properties: {
    title: string;
    description?: string;
    fileType: string;
    size: number;
    url?: string;
    ownerId: string;
  };
}

export interface ConceptNode extends BaseNode {
  type: NodeType.CONCEPT;
  properties: {
    name: string;
    description?: string;
    category?: string;
    importance: number;
  };
}

export interface PersonNode extends BaseNode {
  type: NodeType.PERSON;
  properties: {
    name: string;
    email?: string;
    role?: string;
    department?: string;
    avatarUrl?: string;
  };
}

export interface TopicNode extends BaseNode {
  type: NodeType.TOPIC;
  properties: {
    name: string;
    description?: string;
    frequency: number;
    keywords: string[];
  };
}

export interface ConversationNode extends BaseNode {
  type: NodeType.CONVERSATION;
  properties: {
    conversation_id: string;
    title: string;
    userId: string;
    organizationId: string;
    messageCount?: number;
  };
}

// Future todo node type (commented for documentation)
/*
export interface TodoNode extends BaseNode {
  type: NodeType.TODO;
  properties: {
    title: string;
    description?: string;
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
    priority: 'low' | 'medium' | 'high' | 'critical';
    dueDate?: Date;
    assigneeId?: string;
    assigneeName?: string;
    projectId?: string;
    tags?: string[];
    completedAt?: Date;
    completionPercentage?: number;
  };
}
*/

// Union type for all nodes
export type GraphNode =
  | MessageNode
  | ConversationNode
  | DocumentNode
  | ConceptNode
  | PersonNode
  | TopicNode;
  // | TodoNode; // Add when implemented

// Edge interface
export interface GraphEdge {
  id: string;
  source: string; // Node ID
  target: string; // Node ID
  type: EdgeType;
  label?: string;
  weight?: number;
  metadata?: Record<string, any>;
  createdAt: Date;
}

// Graph statistics
export interface GraphStats {
  nodeCount: number;
  edgeCount: number;
  nodesByType: Record<NodeType, number>;
  edgesByType: Record<EdgeType, number>;
  averageDegree: number;
  density: number;
  connectedComponents: number;
  lastUpdated: Date;
}

// Query types
export interface GraphQuery {
  naturalLanguage?: string;
  filters?: {
    nodeTypes?: NodeType[];
    edgeTypes?: EdgeType[];
    dateRange?: {
      start: Date;
      end: Date;
    };
    keywords?: string[];
  };
  limit?: number;
  depth?: number;
}

export interface GraphQueryResult {
  nodes: GraphNode[];
  edges: GraphEdge[];
  matchedNodeIds?: string[];  // NEW: Node IDs for graph highlighting
  executionTime: number;
  truncated: boolean;
}

// Cytoscape element format
export interface CytoscapeNode {
  data: {
    id: string;
    label: string;
    type: string;
    [key: string]: any;
  };
  position?: {
    x: number;
    y: number;
  };
  classes?: string;
}

export interface CytoscapeEdge {
  data: {
    id: string;
    source: string;
    target: string;
    label?: string;
    type: string;
    [key: string]: any;
  };
  classes?: string;
}

export interface CytoscapeElements {
  nodes: CytoscapeNode[];
  edges: CytoscapeEdge[];
}

// Layout options
export interface GraphLayoutOptions {
  name: 'fcose' | 'cose-bilkent' | 'circle' | 'grid' | 'breadthfirst' | 'concentric';
  animate: boolean;
  animationDuration: number;
  fit: boolean;
  padding: number;
  // Layout-specific options
  [key: string]: any;
}

// Export options
export interface GraphExportOptions {
  format: 'png' | 'svg' | 'json' | 'graphml' | 'gexf' | 'csv';
  quality?: number;
  background?: string;
  includeStyles?: boolean;
}

// Extended export options with filter and annotation support
export interface GraphExportOptionsExtended extends GraphExportOptions {
  format: 'png' | 'svg' | 'json' | 'graphml' | 'gexf' | 'csv';
  applyCurrentFilters?: boolean;
  includeAnnotations?: boolean;
  temporalRange?: { start: string; end: string };
  includeMetadata?: boolean;
  includeStatistics?: boolean;
  filters?: {
    nodeTypes?: string[];
    dateRange?: { start: string; end: string };
    searchTerm?: string;
    minDegree?: number;
  };
}

// Temporal metadata returned from temporal queries
export interface TemporalMetadata {
  oldestDate: string;
  newestDate: string;
  distribution: Record<string, number>; // period key -> node count
  granularity: 'day' | 'week' | 'month';
  totalNodes: number;
}

// Graph annotation for collaborative commenting on nodes/edges
export interface GraphAnnotation {
  id: string;
  nodeId?: string;
  edgeId?: string;
  userId: string;
  userName: string;
  content: string;
  parentAnnotationId?: string; // For threading
  mentions?: string[];         // @mention user IDs
  status: 'open' | 'resolved';
  positionX?: number;
  positionY?: number;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  replies?: GraphAnnotation[]; // Nested replies for threading
}

// Filter options for UI
export interface GraphFilterOptions {
  showNodeTypes?: Set<NodeType | string>;
  showEdgeTypes?: Set<EdgeType | string>;
  minNodeDegree?: number;
  dateRange?: {
    start: Date;
    end: Date;
  };
  searchTerm?: string;
}